import React, { useState } from "react";
import { useIntl } from "react-intl";
import { Box, Button, Stack, Typography } from "@mui/material";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { AddressColumnTitle } from "../../../../../common/Addresses/AddressFields";
import { HiddenFieldsToggle, InlineOverrideWarning, OverrideCountChip } from "../../components/primitives";

import { getAddressOverrideDetails } from "../../model/overrideModel";
import { panelSx, ui } from "../../styles";
import DraggableAddressLines from "./DraggableAddressLines";

const AddressFields = ({ address,
  addressSettings,
  addressType,
  contentDisabled = false,
  headerControl = null,
  masterAddress,
  masterAddressSettings,
  moveAddressLine,
  setAddressFieldSetting,
  setAddressFieldValue,
  updateAddress }) => {

  const intl = useIntl(),
    t = (id, values) => intl.formatMessage({ id }, values),
    [showHidden, setShowHidden] = useState(false);

  const details = getAddressOverrideDetails(
    address,
    //addressSettings,
    masterAddress,
    //masterAddressSettings
  );

  const orderedFieldKeys = Object.entries(addressSettings || {})
    .sort(
      ([, a], [, b]) =>
        Number(a?.displayOrder ?? 0) -
        Number(b?.displayOrder ?? 0)
    )
    .map(([fieldKey]) => fieldKey);

  const hiddenFields = orderedFieldKeys.filter(
    fieldKey => addressSettings[fieldKey]?.type === "HIDDEN"
  );

  const hiddenDataFields = hiddenFields.filter(
    fieldKey => String(address?.[fieldKey] ?? "").trim() !== ""
  );

  const billing = addressType === "invoiceAddress";

  const header = <Stack direction="row" spacing={.75} alignItems="center" justifyContent="space-between" sx={{ minHeight: 24, mb: .75 }}>
    <AddressColumnTitle title={t(billing ? "admin.crm.customerCard.addresses.billingAddress" : "admin.crm.customerCard.addresses.deliveryAddress")} icon={billing ? <ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} /> : <LocalShippingOutlinedIcon sx={{ fontSize: 14 }} />} />
    <Stack direction="row" spacing={.5} alignItems="center" sx={{ minWidth: 0 }}>{headerControl}{!contentDisabled && <OverrideCountChip count={details.total} />}
      {!contentDisabled && details.orderChanged && <Button size="small" startIcon={<RestartAltRoundedIcon sx={{ fontSize: "12px !important" }} />} onClick={() => { }}
        sx={{ minHeight: 20, px: .3, color: "#b54708", textTransform: "none", fontSize: 8.7, fontWeight: 600 }}>{t("admin.crm.customerCard.addresses.resetOrder")}</Button>}
    </Stack>
  </Stack>;

  if (contentDisabled) return <Box sx={panelSx}>{header}<Box sx={{ minHeight: 64, display: "flex", alignItems: "center", px: 1, py: .8, border: `1px dashed ${ui.borderStrong}`, borderRadius: 1, bgcolor: ui.soft }}>
    <Typography sx={{ color: ui.muted, fontSize: 9.6, lineHeight: 1.45 }}>{t("admin.crm.customerCard.addresses.sameAsBillingDescription")}</Typography>
  </Box></Box>;

  return <Box sx={panelSx}>{header}
    {hiddenDataFields.length > 0 && <Stack direction={{ xs: "column", sm: "row" }} spacing={.55} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between"
      sx={{ mb: .75, px: .75, py: .55, borderRadius: .9, bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
      <Stack direction="row" spacing={.45} alignItems="center"><WarningAmberRoundedIcon sx={{ color: "#b54708", fontSize: 13 }} />
        <Typography sx={{ color: "#9a3412", fontSize: 9.2, fontWeight: 550, lineHeight: 1.35 }}>{t("admin.crm.customerCard.addresses.hidden.hasData", { count: hiddenDataFields.length })}</Typography>
      </Stack><HiddenFieldsToggle count={hiddenFields.length} showHidden={showHidden} onClick={() => setShowHidden(true)} warning />
    </Stack>}
    {hiddenFields.length > 0 && ((hiddenDataFields.length === 0) || (showHidden && hiddenDataFields.length > 0)) && <Stack direction="row" justifyContent="flex-end" sx={{ mb: .45 }}>
      <HiddenFieldsToggle count={hiddenFields.length} showHidden={showHidden} onClick={() => setShowHidden(value => !value)} />
    </Stack>}
    <DraggableAddressLines
      address={address}
      addressSettings={addressSettings}
      addressType={addressType}
      masterAddress={masterAddress}
      masterAddressSettings={masterAddressSettings}
      moveAddressLine={moveAddressLine}
      setAddressFieldSetting={setAddressFieldSetting}
      setAddressFieldValue={setAddressFieldValue}
      showHidden={showHidden}
      updateAddress={updateAddress}
    />
    {details.orderChanged && <InlineOverrideWarning>{t("admin.crm.customerCard.addresses.orderChanged")}</InlineOverrideWarning>}
    {details.settingCount > 0 && <InlineOverrideWarning>{t("admin.crm.customerCard.addresses.settingsChanged", { count: details.settingCount })}</InlineOverrideWarning>}
    <Typography sx={{ ml: 2.8, mt: .4, color: ui.subtle, fontSize: 8.9, lineHeight: 1.4 }}>{t("admin.crm.customerCard.addresses.overrideNote")}</Typography>
  </Box>;
};

export default AddressFields;
